import { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eraser, PenTool, Type } from 'lucide-react';

const SignatureCapture = ({ onSignatureChange, language }) => {
  const sigCanvas = useRef(null);
  const [activeTab, setActiveTab] = useState('draw');
  const [typedName, setTypedName] = useState('');

  const clearCanvas = () => {
    sigCanvas.current?.clear();
    onSignatureChange(null, null);
  };

  const handleCanvasEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const dataUrl = sigCanvas.current.toDataURL();
      onSignatureChange('canvas', dataUrl);
    }
  };

  const handleTypedNameChange = (e) => {
    const name = e.target.value;
    setTypedName(name);
    if (name.trim()) {
      onSignatureChange('typed', name);
    } else {
      onSignatureChange(null, null);
    }
  };

  const translations = {
    en: {
      signTitle: 'Signature',
      drawTab: 'Draw',
      typeTab: 'Type Name',
      drawInstruction: 'Draw your signature in the box below',
      typeInstruction: 'Type your full name',
      clearButton: 'Clear',
      typePlaceholder: 'Your Full Name'
    },
    el: {
      signTitle: 'Υπογραφή',
      drawTab: 'Σχεδίαση',
      typeTab: 'Πληκτρολόγηση Ονόματος',
      drawInstruction: 'Σχεδιάστε την υπογραφή σας στο παρακάτω πλαίσιο',
      typeInstruction: 'Πληκτρολογήστε το πλήρες όνομά σας',
      clearButton: 'Καθαρισμός',
      typePlaceholder: 'Το Πλήρες Όνομά σας'
    },
    he: {
      signTitle: 'חתימה',
      drawTab: 'צייר',
      typeTab: 'הקלד שם',
      drawInstruction: 'צייר את החתימה שלך בתיבה למטה',
      typeInstruction: 'הקלד את שמך המלא',
      clearButton: 'נקה',
      typePlaceholder: 'השם המלא שלך'
    }
  };

  const t = translations[language] || translations.en;

  return (
    <div className="space-y-4">
      <Label className="text-base font-semibold">{t.signTitle} *</Label>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="draw">
            <PenTool className="w-4 h-4 mr-2" />
            {t.drawTab}
          </TabsTrigger>
          <TabsTrigger value="type">
            <Type className="w-4 h-4 mr-2" />
            {t.typeTab}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="draw" className="space-y-3">
          <p className="text-sm text-gray-600">{t.drawInstruction}</p>
          <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
            <SignatureCanvas
              ref={sigCanvas}
              canvasProps={{
                className: 'w-full h-48',
                style: { touchAction: 'none' }
              }}
              onEnd={handleCanvasEnd}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clearCanvas}
          >
            <Eraser className="w-4 h-4 mr-2" />
            {t.clearButton}
          </Button>
        </TabsContent>
        
        <TabsContent value="type" className="space-y-3">
          <p className="text-sm text-gray-600">{t.typeInstruction}</p>
          <Input
            value={typedName}
            onChange={handleTypedNameChange}
            placeholder={t.typePlaceholder}
            className="text-2xl font-serif italic"
            style={{ fontFamily: "'Dancing Script', cursive" }}
          />
          {typedName && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Preview:</p>
              <p className="text-4xl font-serif italic" style={{ fontFamily: "'Dancing Script', cursive" }}>
                {typedName}
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SignatureCapture;
